import React, { useEffect, useMemo, useRef, useState } from "react";
import { ApiUrl } from "../../../ApiUrl";
import "./AdmChatBot.css";

const SUGGESTIONS = [
  "How many students are there in each class?",
  "What is today's attendance summary?",
  "How much fee was collected today?",
  "Show open grievances",
];

const initialMessage = {
  role: "bot",
  text: "Hi! I can answer questions about students, staff, attendance, fees, classes, grievances, visitors, and school modules.",
  source: "Ready",
};

const AdmChatBot = () => {
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([initialMessage]);
  const logRef = useRef(null);

  const contextPayload = useMemo(() => {
    const orgId = localStorage.getItem("orgId") || "";
    const branchId = localStorage.getItem("branchId") || "";
    const academicSessionId = localStorage.getItem("academicSessionId") || "";
    const loginId = sessionStorage.getItem("loginId") || sessionStorage.getItem("userId") || "";
    return {
      login_id: loginId ? Number(loginId) : null,
      org_id: orgId ? Number(orgId) : null,
      branch_id: branchId ? Number(branchId) : null,
      academic_year_id: academicSessionId ? Number(academicSessionId) : null,
      date: (() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      })(),
    };
  }, []);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [messages, loading]);

  const askChatBot = async (questionOverride = null) => {
    const trimmed = (questionOverride ?? question).trim();
    if (!trimmed || loading) return;

    const previousQuestion = [...messages].reverse().find((msg) => msg.role === "user")?.text || "";
    setMessages((prev) => [...prev, { role: "user", text: trimmed }]);
    setQuestion("");
    setLoading(true);

    try {
      const response = await fetch(`${ApiUrl.apiurl}AdminChatBot/ask/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: trimmed, previous_question: previousQuestion, ...contextPayload }),
      });
      const responseText = await response.text();
      let result = null;
      try {
        result = responseText ? JSON.parse(responseText) : null;
      } catch {
        result = null;
      }
      if (!response.ok) {
        throw new Error(
          result?.error || result?.message ||
          (response.status === 504
            ? "The assistant took too long to respond. Please try a shorter question."
            : "The assistant request failed.")
        );
      }

      setMessages((prev) => [
        ...prev,
        {
          role: "bot",
          text: result?.data?.reply || "I could not find an answer for that question.",
          results: result?.data?.results || null,
        },
      ]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { role: "bot", text: error.message || "I could not connect to the school assistant.", source: "Assistant error" },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="admin-chatbot-page">
      <section className="admin-chatbot-card" aria-label="School AI assistant">
        <header className="admin-chatbot-header">
          <div className="admin-chatbot-brand">
            <div className="admin-chatbot-icon" aria-hidden="true">✦</div>
            <div>
              <div className="admin-chatbot-eyebrow">School management assistant</div>
              <h1>Ask your school data</h1>
              <p>Get clear answers from the current organization, branch, and academic session.</p>
            </div>
          </div>
        </header>

        <div className="admin-chatbot-content">
          <div className="admin-chatbot-suggestions" aria-label="Suggested questions">
            <span>Try asking</span>
            {SUGGESTIONS.map((suggestion) => (
              <button type="button" key={suggestion} onClick={() => askChatBot(suggestion)} disabled={loading}>{suggestion}</button>
            ))}
          </div>

          <div className="admin-chat-log" ref={logRef} aria-live="polite">
            {messages.map((msg, idx) => {
              const columns = msg.results?.columns || (msg.results?.rows?.[0] ? Object.keys(msg.results.rows[0]) : []);
              return (
                <div key={`${msg.role}-${idx}`} className={`admin-chat-message ${msg.role}`}>
                  <div className="admin-chat-avatar" aria-hidden="true">{msg.role === "bot" ? "✦" : "You"}</div>
                  <div className="admin-chat-bubble">
                    <div className="admin-chat-text">{msg.text}</div>
                    {msg.results?.rows?.length > 0 && (
                      <div className="admin-chat-results">
                        <div className="admin-chat-table-wrap">
                          <table>
                            <thead><tr>{columns.map((column) => <th key={column}>{column}</th>)}</tr></thead>
                            <tbody>
                              {msg.results.rows.slice(0, 25).map((row, rowIndex) => (
                                <tr key={rowIndex}>{columns.map((column) => <td key={column}>{String(row[column] ?? "")}</td>)}</tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {msg.results.truncated && <div className="admin-chat-note">Showing the first 25 rows.</div>}
                      </div>
                    )}

                  </div>
                </div>
              );
            })}
            {loading && (
              <div className="admin-chat-message bot">
                <div className="admin-chat-avatar" aria-hidden="true">✦</div>
                <div className="admin-chat-bubble admin-chat-thinking">
                  Checking the school data<span>...</span>
                  <small> Common questions answer instantly; unusual ones can take up to a minute.</small>
                </div>
              </div>
            )}
          </div>

          <div className="admin-chat-composer">
            <textarea
              rows={2}
              value={question}
              placeholder="Ask about students, attendance, fees, classes, or any school module..."
              onChange={(event) => setQuestion(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  askChatBot();
                }
              }}
              disabled={loading}
              aria-label="Ask the school assistant"
            />
            <button type="button" onClick={() => askChatBot()} disabled={loading || !question.trim()}>{loading ? "Checking..." : "Send"}</button>
          </div>
          <div className="admin-chat-hint">Press Enter to send · Shift + Enter for a new line</div>
        </div>
      </section>
    </main>
  );
};

export default AdmChatBot;
